import { isTimelinePlaybackPerfEnabled, recordTimelinePlaybackPerf } from '../perfHooks'

const DIAGNOSTIC_RESPONSE_HEADERS = [
  'content-length',
  'content-range',
  'accept-ranges',
  'content-type',
  'cache-control',
  'etag',
  'age',
  'cache-status',
  'cf-cache-status',
  'cdn-cache-status',
  'x-cache',
  'x-cache-status',
  'x-vercel-cache',
] as const

const CORS_SAFELISTED_RESPONSE_HEADERS = new Set([
  'cache-control',
  'content-language',
  'content-length',
  'content-type',
  'expires',
  'last-modified',
  'pragma',
])

export type PlaybackMediaPathKind =
  'blob-url' | 'direct-storage-cdn' | 'storage-rewrite' | 'proxy-media' | 'other-proxy' | 'data-url' | 'unknown'

let pipelineCounter = 0
let requestCounter = 0

export function nextPlaybackPipelineId(assetId: string): string {
  pipelineCounter += 1
  return `${assetId}:${pipelineCounter}`
}

export function isPlaybackDiagnosticsEnabled(): boolean {
  return isTimelinePlaybackPerfEnabled()
}

export function classifyPlaybackMediaPath(url: string): PlaybackMediaPathKind {
  if (url.startsWith('blob:')) return 'blob-url'
  if (url.startsWith('data:')) return 'data-url'

  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined)
    const pathname = parsed.pathname.toLowerCase()
    if (pathname.includes('proxy-media')) return 'proxy-media'
    if (pathname.startsWith('/_storage') || pathname.startsWith('/storage')) return 'storage-rewrite'

    if (typeof window !== 'undefined' && parsed.origin === window.location.origin) {
      if (pathname.startsWith('/api/') || pathname.includes('proxy')) return 'other-proxy'
      return 'other-proxy'
    }

    return 'direct-storage-cdn'
  } catch {
    return 'unknown'
  }
}

function requestUrl(input: RequestInfo | URL): string | null {
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url
  if (input instanceof URL) return input.toString()
  if (typeof input === 'string') return input
  return null
}

function requestHeader(input: RequestInfo | URL, init: RequestInit | undefined, name: string): string | null {
  try {
    if (init?.headers) {
      const fromInit = new Headers(init.headers).get(name)
      if (fromInit) return fromInit
    }
  } catch {}

  try {
    if (typeof Request !== 'undefined' && input instanceof Request) return input.headers.get(name)
  } catch {}

  return null
}

function isSameOrigin(url: string | null): boolean {
  if (!url || typeof window === 'undefined') return true
  try {
    return new URL(url, window.location.href).origin === window.location.origin
  } catch {
    return true
  }
}

function headerDiagnostics(response: Response, sameOrigin: boolean): Record<string, { state: string; value?: string }> {
  const headers: Record<string, { state: string; value?: string }> = {}

  for (const name of DIAGNOSTIC_RESPONSE_HEADERS) {
    const value = response.headers.get(name)
    if (value !== null) {
      headers[name] = { state: 'readable', value: value.slice(0, 160) }
    } else if (sameOrigin || CORS_SAFELISTED_RESPONSE_HEADERS.has(name)) {
      headers[name] = { state: 'missing' }
    } else {
      headers[name] = { state: 'not-exposed' }
    }
  }

  return headers
}

function parseRequestedBytes(range: string | null): number | null {
  if (!range) return null
  const match = range.match(/^bytes=(\d+)-(\d+)?$/i)
  if (!match) return null

  const start = Number(match[1])
  if (!Number.isFinite(start)) return null

  if (!match[2]) return null
  const end = Number(match[2])
  if (!Number.isFinite(end) || end < start) return null
  return end - start + 1
}

function responseKind(status: number, range: string | null): string {
  if (!range) return status === 206 ? 'unexpected-byte-range' : 'full-download'
  if (status === 206) return 'byte-range'
  if (status === 200) return 'full-download-fallback'
  return 'range-error'
}

export function createDiagnosticFetchFn({
  assetId,
  pipelineId,
  sourceUrl,
}: {
  assetId: string
  pipelineId: string
  sourceUrl: string
}): typeof fetch | undefined {
  if (!isPlaybackDiagnosticsEnabled()) return undefined

  const pathKind = classifyPlaybackMediaPath(sourceUrl)
  const attemptsByRange = new Map<string, number>()

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    requestCounter += 1
    const requestId = `${pipelineId}:fetch:${requestCounter}`
    const startedAt = performance.now()
    const range = requestHeader(input, init, 'range')
    const attemptKey = range ?? 'full'
    const attemptCount = (attemptsByRange.get(attemptKey) ?? 0) + 1
    attemptsByRange.set(attemptKey, attemptCount)
    const url = requestUrl(input)
    const sameOrigin = isSameOrigin(url)

    try {
      const response = await fetch(input, init)
      const elapsedMs = performance.now() - startedAt
      recordTimelinePlaybackPerf({
        type: 'url-source-fetch',
        assetId,
        pipelineId,
        requestId,
        elapsedMs,
        status: response.status,
        ok: response.ok,
        requestRange: range,
        requestedBytes: parseRequestedBytes(range),
        retryCount: attemptCount - 1,
        requestKind: range ? 'range' : 'full',
        responseKind: responseKind(response.status, range),
        pathKind,
        headers: headerDiagnostics(response, sameOrigin),
      })
      return response
    } catch (error) {
      recordTimelinePlaybackPerf({
        type: 'url-source-fetch',
        assetId,
        pipelineId,
        requestId,
        elapsedMs: performance.now() - startedAt,
        status: null,
        ok: false,
        requestRange: range,
        requestedBytes: parseRequestedBytes(range),
        retryCount: attemptCount - 1,
        requestKind: range ? 'range' : 'full',
        responseKind: 'network-error',
        pathKind,
        headers: {},
        errorName: error instanceof Error ? error.name || error.message : String(error),
      })
      throw error
    }
  }
}
